/**
 * CleanNest - Shopify Theme JavaScript
 */

(function () {
  'use strict';

  /* ========================================
     UTILITIES
     ======================================== */

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  function debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function showToast(message) {
    const toast = $('[data-toast]');
    const toastMessage = $('[data-toast-message]');
    if (!toast || !toastMessage) return;
    toastMessage.textContent = message;
    toast.classList.remove('toast--hidden');
    setTimeout(() => toast.classList.add('toast--hidden'), 3000);
  }

  /* ========================================
     MOBILE MENU
     ======================================== */

  function initMobileMenu() {
    const toggleBtn = $('[data-mobile-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    const overlay = $('[data-mobile-menu-overlay]');
    const closeBtn = $('[data-mobile-menu-close]');
    if (!toggleBtn || !menu) return;

    function open() {
      menu.setAttribute('data-open', '');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      menu.removeAttribute('data-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    toggleBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
  }

  /* ========================================
     CART DRAWER
     ======================================== */

  function initCartDrawer() {
    const drawer = $('[data-cart-drawer]');
    const overlay = $('[data-cart-overlay]');
    const closeBtn = $('[data-cart-close]');
    const toggleBtns = $$('[data-cart-toggle]');
    if (!drawer) return;

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    toggleBtns.forEach((btn) => btn.addEventListener('click', open));
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);

    // Quick add forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-quick-add]');
      if (!form) return;
      e.preventDefault();

      const formData = new FormData(form);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });
        if (res.ok) {
          await updateCartDrawer();
          open();
          showToast('Added to cart');
        } else {
          const data = await res.json();
          showToast(data.description || 'Could not add to cart');
        }
      } catch {
        // Fallback: submit normally
        form.submit();
      }
    });

    // Cart quantity controls in drawer
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-cart-quantity-minus]');
      const plus = e.target.closest('[data-cart-quantity-plus]');
      const remove = e.target.closest('[data-cart-remove]');

      if (minus) {
        e.preventDefault();
        const key = minus.dataset.key;
        const input = $(`[data-cart-quantity-input][data-key="${key}"]`);
        const newVal = Math.max(0, parseInt(input.value, 10) - 1);
        updateCartItem(key, newVal);
      }

      if (plus) {
        e.preventDefault();
        const key = plus.dataset.key;
        const input = $(`[data-cart-quantity-input][data-key="${key}"]`);
        const newVal = parseInt(input.value, 10) + 1;
        updateCartItem(key, newVal);
      }

      if (remove) {
        e.preventDefault();
        updateCartItem(remove.dataset.key, 0);
      }
    });
  }

  async function updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (res.ok) {
        await updateCartDrawer();
        showToast(quantity === 0 ? 'Removed from cart' : 'Cart updated');
      }
    } catch {
      showToast('Could not update cart');
    }
  }

  async function updateCartDrawer() {
    try {
      const res = await fetch('/?section_id=cart-drawer');
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = $('[data-cart-content]', doc);
      const currentContent = $('[data-cart-content]');
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
      // Update cart count badge
      const cartData = await fetch('/cart.js').then((r) => r.json());
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cartData.item_count;
        el.style.display = cartData.item_count > 0 ? 'flex' : 'none';
      });
    } catch {
      // Silent fail
    }
  }

  /* ========================================
     VARIANT SELECTOR
     ======================================== */

  function initVariantSelector() {
    const selectors = $$('[data-variant-input]');
    if (selectors.length === 0) return;

    function updateVariant() {
      const variantSelect = $('[data-variant-select]');
      if (!variantSelect) return;

      // Build option string from current selections
      const options = [];
      $$('[data-variant-input]').forEach((input) => {
        if (input.type === 'radio') {
          if (input.checked) options.push(input.value);
        } else if (input.tagName === 'SELECT') {
          options.push(input.value);
        }
      });

      // Find matching variant
      const optionEls = [...variantSelect.options];
      const matched = optionEls.find((opt) => {
        const optText = opt.textContent.trim();
        return options.every((val) => optText.includes(val));
      });

      if (matched) {
        variantSelect.value = matched.value;
        const variantIdInput = $('[data-variant-id]');
        if (variantIdInput) variantIdInput.value = matched.value;

        // Update price
        const price = parseInt(matched.dataset.price, 10);
        const comparePrice = parseInt(matched.dataset.comparePrice, 10) || 0;
        const priceEl = $('[data-price]');
        if (priceEl && price) {
          const formatted = formatMoney(price);
          if (comparePrice > price) {
            const formattedCompare = formatMoney(comparePrice);
            const savings = formatMoney(comparePrice - price);
            priceEl.innerHTML = `
              <div class="price__sale">
                <span class="price__regular">${formatted}</span>
                <s class="price__compare">${formattedCompare}</s>
              </div>
              <span class="price__savings">Save ${savings}</span>
            `;
          } else {
            priceEl.innerHTML = `<span class="price__regular">${formatted}</span>`;
          }
        }

        // Update inventory
        const inventory = parseInt(matched.dataset.inventory, 10);
        const available = matched.dataset.available === 'true';
        const inventoryEl = $('[data-inventory]');
        if (inventoryEl) {
          if (!available) {
            inventoryEl.innerHTML = '<span class="inventory-status inventory-status--out">Out of Stock</span>';
          } else if (inventory > 10) {
            inventoryEl.innerHTML = '<span class="inventory-status inventory-status--in-stock">In Stock</span>';
          } else if (inventory > 0) {
            inventoryEl.innerHTML = `<span class="inventory-status inventory-status--low">Only ${inventory} left</span>`;
          } else {
            inventoryEl.innerHTML = '<span class="inventory-status inventory-status--in-stock">Available for backorder</span>';
          }
        }

        // Update add to cart button
        const addBtn = $('[data-add-to-cart]');
        const addBtnText = $('[data-add-to-cart-text]');
        if (addBtn && addBtnText) {
          if (available) {
            addBtn.disabled = false;
            addBtnText.textContent = 'Add to Cart';
          } else {
            addBtn.disabled = true;
            addBtnText.textContent = 'Sold Out';
          }
        }
      }
    }

    selectors.forEach((input) => {
      input.addEventListener('change', updateVariant);
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  /* ========================================
     PRODUCT MEDIA THUMBS
     ======================================== */

  function initProductMedia() {
    const thumbs = $$('[data-media-thumb]');
    if (thumbs.length === 0) return;

    const featuredImg = $('[data-product-featured-image]');

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.mediaSrc;
        if (featuredImg && src) {
          featuredImg.src = src;
        }
        thumbs.forEach((t) => t.classList.remove('product-media__thumb--active'));
        thumb.classList.add('product-media__thumb--active');
      });
    });
  }

  /* ========================================
     QUANTITY SELECTOR
     ======================================== */

  function initQuantitySelector() {
    document.addEventListener('click', (e) => {
      const minus = e.target.closest('[data-quantity-minus]');
      const plus = e.target.closest('[data-quantity-plus]');
      if (!minus && !plus) return;

      const input = $(
        '[data-quantity-input]',
        (minus || plus).closest('.quantity-selector')
      );
      if (!input) return;

      let val = parseInt(input.value, 10) || 1;
      const min = parseInt(input.min, 10) || 1;
      const max = parseInt(input.max, 10) || 99;

      if (minus) {
        val = Math.max(min, val - 1);
      } else {
        val = Math.min(max, val + 1);
      }

      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ========================================
     SIZE CHART MODAL
     ======================================== */

  function initSizeChart() {
    const toggle = $('[data-size-chart-toggle]');
    const modal = $('[data-size-chart]');
    const overlay = $('[data-size-chart-overlay]');
    const closeBtn = $('[data-size-chart-close]');
    if (!toggle || !modal) return;

    function open() {
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
  }

  function initCollectionSort() {
    const sortSelect = $('[data-sort-by]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }
  }

  function initProductRecommendations() {
    const container = $('[data-product-recommendations]');
    if (!container || !container.dataset.url) return;

    fetch(container.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const recommendations = $('[data-product-recommendations]', doc);
        if (recommendations?.innerHTML.trim()) {
          container.innerHTML = recommendations.innerHTML;
        }
      })
      .catch(() => {
        // Recommendations are optional; keep the page usable if loading fails.
      });
  }

  /* ========================================
     NEWSLETTER FORM
     ======================================== */

  function initNewsletterForm() {
    $$('.newsletter-form').forEach((form) => {
      form.addEventListener('submit', (e) => {
        // Let Shopify handle it, just show toast on success
        const successEl = form.querySelector('.newsletter-form__success');
        if (successEl) {
          showToast('Thanks for subscribing!');
        }
      });
    });
  }

  /* ========================================
     ANNOUNCEMENT BAR CLOSE
     ======================================== */

  function initAnnouncementBar() {
    const closeBtn = $('[data-announcement-close]');
    const bar = closeBtn?.closest('.announcement-bar');
    if (!closeBtn || !bar) return;

    closeBtn.addEventListener('click', () => {
      bar.style.display = 'none';
    });
  }

  /* ========================================
     TOAST CLOSE
     ======================================== */

  function initToast() {
    const closeBtn = $('[data-toast-close]');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', () => {
      $('[data-toast]')?.classList.add('toast--hidden');
    });
  }

  /* ========================================
     ADD TO CART WITH AJAX
     ======================================== */

  function initAddToCart() {
    const productForm = $('#product-form');
    if (!productForm) return;

    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(productForm);
      const submitBtn = productForm.querySelector('[data-add-to-cart]');
      const originalText = submitBtn?.textContent;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.querySelector('[data-add-to-cart-text]').textContent = 'Adding...';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (res.ok) {
          await updateCartDrawer();
          showToast('Added to cart');
        } else {
          const data = await res.json();
          showToast(data.description || 'Could not add to cart');
        }
      } catch {
        // Fallback: submit normally
        productForm.submit();
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          if (submitBtn.querySelector('[data-add-to-cart-text]')) {
            submitBtn.querySelector('[data-add-to-cart-text]').textContent = originalText;
          }
        }
      }
    });
  }

  /* ========================================
     INITIALIZE
     ======================================== */

  function init() {
    initMobileMenu();
    initCartDrawer();
    initVariantSelector();
    initProductMedia();
    initQuantitySelector();
    initSizeChart();
    initCollectionSort();
    initProductRecommendations();
    initNewsletterForm();
    initAnnouncementBar();
    initToast();
    initAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
